import { Vector3 } from 'three';
import { Room, type ObstacleBox, type PortalInput, type ZoneInput } from './Room';
import { ALL_SURFACE_IDS, type SurfaceId } from './Surface';
import type { LevelDefinition } from './LevelDefinition';

export interface BuiltLevel {
  room: Room;
  definition: LevelDefinition;
  spawn: { surface: string; position: Vector3; facing: number };
  targets: Array<{
    surface: string;
    position: Vector3;
    magnetic?: boolean;
    route?: Array<[number, number, number]>;
  }>;
}

/** Turn a validated level definition into a room plus spawn and target data. */
export function buildLevel(definition: LevelDefinition): BuiltLevel {
  const [width, height, depth] = definition.size;

  const magnetic: Partial<Record<SurfaceId, boolean>> = {};
  const enabled: Partial<Record<SurfaceId, boolean>> = {};
  for (const id of ALL_SURFACE_IDS) {
    const surface = definition.surfaces[id];
    magnetic[id] = surface.magnetic;
    enabled[id] = surface.enabled;
  }

  const obstacles: ObstacleBox[] = definition.obstacles.map((obstacle) => ({
    center: new Vector3(...obstacle.position),
    halfSize: new Vector3(...obstacle.size).multiplyScalar(0.5),
    magnetic: obstacle.magnetic ?? false,
    kind: obstacle.kind,
    zoneId: obstacle.zone,
  }));

  const zones: ZoneInput[] = (definition.zones ?? []).map((zone) => {
    const zoneMagnetic: Partial<Record<SurfaceId, boolean>> = {};
    const zoneEnabled: Partial<Record<SurfaceId, boolean>> = {};
    for (const id of ALL_SURFACE_IDS) {
      const override = zone.surfaces?.[id];
      zoneMagnetic[id] = override?.magnetic ?? magnetic[id] ?? true;
      zoneEnabled[id] = override?.enabled ?? enabled[id] ?? true;
    }
    return {
      id: zone.id,
      origin: new Vector3(...zone.origin),
      size: new Vector3(...zone.size),
      magnetic: zoneMagnetic,
      enabled: zoneEnabled,
      hullFaces: zone.hullFaces,
    };
  });

  const portals: PortalInput[] = (definition.portals ?? []).map((portal) => ({
    id: portal.id,
    a: portal.a,
    b: portal.b,
    axis: portal.axis,
    position: new Vector3(...portal.position),
    width: portal.width,
    height: portal.height,
  }));

  const room = new Room(definition.id, width, height, depth, {
    magnetic,
    enabled,
    obstacles,
    zones: zones.length > 0 ? zones : undefined,
    portals: portals.length > 0 ? portals : undefined,
  });

  const defaultZone = zones[0]?.id ?? definition.id;
  const spawnZone = definition.spawn.zone ?? defaultZone;
  const spawnKey = room.keyFor(spawnZone, definition.spawn.surface);
  const spawnSurface = room.getSurface(spawnKey);
  const spawnSurfaceKey = spawnSurface !== undefined ? spawnKey : room.keyFor(spawnZone, 'floor');

  return {
    room,
    definition,
    spawn: {
      surface: spawnSurfaceKey,
      position: new Vector3(...definition.spawn.position),
      facing: definition.spawn.facing ?? 0,
    },
    targets: definition.targets.map((target) => {
      const zone = target.zone ?? defaultZone;
      const key = room.keyFor(zone, target.surface);
      return {
        surface: room.getSurface(key) !== undefined ? key : room.keyFor(zone, 'floor'),
        position: new Vector3(...target.position),
        magnetic: target.magnetic,
        route: target.route,
      };
    }),
  };
}
